import { Controller } from '@hotwired/stimulus'

// Connects to data-controller="form-confirmation"
export default class extends Controller {
  static targets = ['field', 'template', 'submitLabel']

  static values = {
    confirmed: {
      type: Boolean,
      default: false
    },
    confirmationTitle: {
      type: String,
      default: 'TKTK'
    },
    isConfirming: {
      type: Boolean,
      default: false
    },
    dynamicTable: {
      type: Boolean,
      default: false
    },
    formValues: {
      type: Object,
      required: false,
      default: {}
    },
    extraFormValues: {
      type: Object,
      required: false,
      default: {}
    }
  }

  async connect () {
    this.element.addEventListener('turbo:submit-start', this.onSubmit)
    document.body.addEventListener('popover:confirmed', this.onConfirmed)

    if (this.dynamicTableValue) {
      await this.#loadDynamicTable()
    }
  }

  disconnect () {
    if (this.dynamicTableValue) {
      this.#unloadDynamicTable()
    }
  }

  onConfirmed = (event) => {
    if (!this.isConfirmingValue) {
      return
    }

    this.isConfirmingValue = false

    let button = this.lastSubmitButton

    if (!button) {
      button = this.element.querySelector('input[type=submit]')
    }

    if (!button) {
      button = this.element.querySelector('button[type=submit]')
    }

    if (!button) {
      throw new Error('No submit button found for form', this.element)
    }

    this.confirmedValue = true

    this.element.requestSubmit(button)
  }

  onSubmit = (event) => {
    this.lastSubmitButton = event.detail.formSubmission.submitter

    let confirmLabel = 'Submit'

    if (this.lastSubmitButton && this.lastSubmitButton.dataset.confirmLabel) {
      confirmLabel = this.lastSubmitButton.dataset.confirmLabel
    } else if (this.lastSubmitButton && this.lastSubmitButton.value) {
      confirmLabel = this.lastSubmitButton.value
    }

    if (!this.confirmedValue) {
      event.detail.formSubmission.stop()
      this.showConfirmation(confirmLabel)
    }
  }

  extraFormValuesValueChanged = (event) => {
    this.#calculateFormValues()
  }

  closeConfirmation (event) {
    event.preventDefault()

    this.confirmedValue = false

    document.body.dispatchEvent(new CustomEvent('popover:hide'))
  }

  fieldValueChanged (event) {
    this.#calculateFormValues()
  }

  showConfirmation (submitValue = null) {
    if (!this.hasTemplateTarget) {
      return
    }

    this.isConfirmingValue = true

    const labels = this.templateTarget.content.querySelectorAll('[data-form-confirmation-target="submitLabel"]')

    if (labels && submitValue) {
      labels.forEach(label => {
        label.textContent = submitValue
      })
    }

    if (this.dynamicTableVueTarget) {
      const content = this.templateTarget.content

      const slot = content.querySelector('slot[name="form-fields"]')

      if (slot) {
        slot.innerHTML = this.dynamicTableVueTarget.innerHTML
      }
    }

    document.body.dispatchEvent(new CustomEvent('popover:show', {
      detail: {
        template: this.templateTarget,
        title: this.confirmationTitleValue
      }
    }))
  }

  #calculateFormValues () {
    if (!this.reactiveValues) {
      return
    }

    const values = {}

    if (this.hasExtraFormValuesValue) {
      Object.assign(values, this.extraFormValuesValue)
    }

    this.fieldTargets.forEach(field => {
      const label = field.dataset.label || field.name

      if (field.nodeName === 'SELECT') {
        if (field.value === '') {
          values[label] = ''
        } else {
          values[label] = field.options[field.selectedIndex]?.textContent.toString().trim() || ''
        }
      } else {
        values[label] = field.value.toString().trim()
      }
    })

    this.reactiveValues.value = values
  }

  async #loadDynamicTable () {
    const {
      createApp,
      h,
      defineAsyncComponent,
      reactive
    } = await import('vue')

    const FormConfirmationTable = defineAsyncComponent(() => import('../components/form-confirmation-table.vue'))

    this.reactiveValues = reactive({})

    const app = createApp({
      render: () => {
        return h(FormConfirmationTable, {
          values: this.reactiveValues.value
        })
      }
    })

    const vueMountTarget = document.createElement('div')
    vueMountTarget.classList.add('hidden')
    this.element.appendChild(vueMountTarget)
    this.dynamicTableVueTarget = vueMountTarget

    app.mount(vueMountTarget)
    this.dynamicTableInstance = app

    this.element.querySelectorAll('.form-select,.form-input').forEach(input => {
      /* skip if this field already has data-action */
      if (input.hasAttribute('data-action')) {
        return
      }

      if (input.hasAttribute('data-skip-confirmation')) {
        return
      }

      input.setAttribute('data-action', 'input->form-confirmation#fieldValueChanged')
      input.setAttribute('data-form-confirmation-target', 'field')

      if (input.hasAttribute('data-label')) {
        return
      }

      const label = this.element.querySelector(`label[for="${input.id}"]`)

      if (label) {
        input.setAttribute('data-label', label.textContent.toString().trim().replace('*', ''))
      }
    })

    this.#calculateFormValues()
  }

  #unloadDynamicTable () {
    if (this.dynamicTableInstance) {
      this.dynamicTableInstance.unmount()
      this.dynamicTableInstance = null

      this.reactiveValues = null
      this.dynamicTableVueTarget = null
    }
  }
}
